import React, { memo, useCallback, useState } from 'react';
import { LeadContact } from "@/services/leadContactsService";
import { TableRow, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/ui/avatar";
import { Mail, Phone, Building, Eye, Edit, Trash2, MoreHorizontal, MessageSquare, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem,
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import CallConfirmationOverlay from "@/components/shared/CallConfirmationOverlay";
import { extractPrimaryEmail, extractPrimaryPhone, parseContactDataForForms } from '@/utils/contactDataHelpers';

interface ContactRowProps {
  contact: LeadContact;
  onEdit: (contact: LeadContact) => void;
  onDelete: (contact: LeadContact) => void;
  onView: (contact: LeadContact) => void;
  onOutreach: (contact: LeadContact) => void;
}

const ContactRow = memo(({ contact, onEdit, onDelete, onView, onOutreach }: ContactRowProps) => {
  const [isCallOverlayOpen, setIsCallOverlayOpen] = useState(false);

  // Defer menu actions so the dropdown fully unmounts before any dialog opens,
  // preventing Radix from leaving pointer-events locked on the body.
  const runAction = useCallback((action: () => void) => {
    setTimeout(action, 0);
  }, []);

  const handlePhoneClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCallOverlayOpen(true);
  }, []);


  const getInitials = (contact: LeadContact) => {
    if (contact.name && contact.name.trim()) {
      const nameParts = contact.name.trim().split(' ').filter(part => part.length > 0);
      if (nameParts.length > 1) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    
    if (contact.first_name && contact.last_name) {
      return `${contact.first_name[0]}${contact.last_name[0]}`.toUpperCase();
    }
    
    if (contact.first_name) {
      return contact.first_name.substring(0, 2).toUpperCase();
    }
    
    if (contact.last_name) {
      return contact.last_name.substring(0, 2).toUpperCase();
    }
    
    return "?";
  };

  const fullName = contact.name || `${contact.first_name} ${contact.last_name}`;
  // Use parsed arrays primarily, then fall back to legacy extractors
  const { phone: phoneArray, email: emailArray } = parseContactDataForForms(contact);
  const primaryEmail = emailArray[0]?.email || extractPrimaryEmail(contact.email) || (contact.metadata?.email ?? "");
  const primaryPhone = contact.phone_number || phoneArray[0]?.phone || extractPrimaryPhone(contact.phone) || (contact.metadata?.phone ?? "");
  const isDecisionMaker = contact.decision_maker || contact.is_decision_maker || contact.is_primary;

  // Counts for badges
  const totalPhones = phoneArray.length;
  const totalEmails = emailArray.length;

  const truncateText = (text: string, maxLength: number): string => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <TooltipProvider>
      <TableRow className="hover:bg-muted/50 cursor-pointer" onClick={() => onView(contact)}>
        <TableCell className="py-3 px-2 md:px-4">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-indigo-100 dark:bg-indigo-900/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs md:text-sm font-medium text-indigo-600 dark:text-indigo-400">
                {getInitials(contact)}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <p className="font-medium text-sm truncate">{fullName}</p>
                {isDecisionMaker && (
                  <Crown className="h-3 w-3 flex-shrink-0 text-amber-500" aria-label="Primary contact" />
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate">{contact.designation || 'No designation'}</p>
            </div>
          </div>
        </TableCell>

        <TableCell className="py-3 hidden md:table-cell">
          <div className="text-sm min-w-0">
            <p className="truncate">{contact.company_name || contact.company?.name || '-'}</p>
          </div>
        </TableCell>

        <TableCell className="py-3 px-2 md:px-4">
          <div className="space-y-1 min-w-0">
            {primaryEmail && (
              <div className="flex items-center gap-1 min-w-0">
                <Mail className="h-3 w-3 flex-shrink-0" /> 
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a href={`mailto:${primaryEmail}`} className="text-blue-600 hover:underline truncate text-xs md:text-sm">
                      {truncateText(primaryEmail, 22)}
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{primaryEmail}</p>
                  </TooltipContent>
                </Tooltip>
                {totalEmails > 1 && (
                  <Badge variant="secondary" className="text-[10px] h-4 px-1 flex-shrink-0">
                    +{totalEmails - 1}
                  </Badge>
                )}
              </div>
            )}
            {primaryPhone && (
              <div className="flex items-center gap-1 min-w-0">
                <Phone className="h-3 w-3 flex-shrink-0" /> 
                <button 
                  onClick={handlePhoneClick}
                  className="text-blue-600 hover:underline truncate text-xs md:text-sm cursor-pointer"
                >
                  {primaryPhone}
                </button>
                {totalPhones > 1 && (
                  <Badge variant="secondary" className="text-[10px] h-4 px-1 flex-shrink-0">
                    +{totalPhones - 1}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </TableCell>

        <TableCell className="py-3 px-1 md:px-4">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); runAction(() => onView(contact)); }}>
                <Eye className="mr-2 h-4 w-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); runAction(() => onEdit(contact)); }}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); runAction(() => onOutreach(contact)); }}>
                <MessageSquare className="mr-2 h-4 w-4" />
                Outreach
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-destructive" 
                onSelect={(e) => { e.preventDefault(); runAction(() => onDelete(contact)); }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

        </TableCell>
      </TableRow>

      {/* Call Confirmation Overlay */}
      {isCallOverlayOpen && primaryPhone && (
        <CallConfirmationOverlay
          isOpen={isCallOverlayOpen}
          onConfirm={() => {
            // Handle the call logic here
            console.log(`Calling ${primaryPhone}`);
            setIsCallOverlayOpen(false);
          }}
          onCancel={() => setIsCallOverlayOpen(false)}
          contactName={fullName}
        />
      )}
    </TooltipProvider>
  );
});

ContactRow.displayName = 'ContactRow';

export default ContactRow;